
// src/hooks/useChecagens.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/checagens';
import { CanalChecklist, StatusCanal, StatusTarefa, Tarefa, ChecagemImovel } from '../models/checagens';

const CHECAGENS_QUERY_KEY = 'checagens';

// Hook para buscar uma checagem específica
export const useChecagem = (checagemId?: number) => {
  return useQuery({
    queryKey: [CHECAGENS_QUERY_KEY, checagemId],
    queryFn: () => api.getChecagem(checagemId!).then(res => res.data),
    enabled: !!checagemId,
  });
};

// Hook para buscar a lista de checagens
export const useChecagens = (params?: any) => {
  return useQuery({ 
    queryKey: [CHECAGENS_QUERY_KEY, params],
    queryFn: () => api.getChecagens(params).then(res => res.data)
  });
};

// Hook para buscar o progresso de uma checagem
export const useChecagemProgresso = (checagemId: number) => {
  return useQuery({
    queryKey: [CHECAGENS_QUERY_KEY, checagemId, 'progresso'],
    queryFn: () => api.getChecagemProgresso(checagemId).then(res => res.data),
    enabled: !!checagemId,
  });
};

// Hook para buscar os imóveis de uma checagem
export const useChecagemImoveis = (checagemId: number, params?: { status_workflow?: string, page?: number, pageSize?: number }) => {
  return useQuery({
    queryKey: [CHECAGENS_QUERY_KEY, checagemId, 'imoveis', params],
    queryFn: () => api.getChecagemImoveis(checagemId, params).then(res => res.data),
    enabled: !!checagemId,
  });
};

// Hook para buscar um imóvel de checagem específico
export const useChecagemImovel = (checagemImovelId: number) => {
  return useQuery<ChecagemImovel>({
    queryKey: ['checagemImovel', checagemImovelId],
    queryFn: () => api.getChecagemImovel(checagemImovelId).then(res => res.data),
    enabled: !!checagemImovelId,
  });
};

// Hook para buscar o checklist de um imóvel da checagem
export const useChecklist = (checagemImovelId: number) => {
  return useQuery({
    queryKey: ['checagemImovel', checagemImovelId, 'checklist'],
    queryFn: () => api.getChecklist(checagemImovelId).then(res => res.data),
    enabled: !!checagemImovelId,
  });
};

// Hook para buscar as tarefas de um imóvel da checagem
export const useTarefas = (checagemImovelId: number) => {
    return useQuery<Tarefa[]>({
        queryKey: ['checagemImovel', checagemImovelId, 'tarefas'],
        queryFn: () => api.getTarefas(checagemImovelId).then(res => res.data),
        enabled: !!checagemImovelId,
    });
};

// Hook para buscar os eventos de um imóvel
export const useEventosImovel = (imovelId: number) => {
  return useQuery({
    queryKey: ['imoveis', imovelId, 'eventos'],
    queryFn: () => api.getEventosImovel(imovelId).then(res => res.data),
    enabled: !!imovelId,
  });
};

// Mutation para atualizar o responsável da checagem
export const useUpdateChecagemResponsavel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, responsavel_usuario_id }: { id: number; responsavel_usuario_id: number }) => 
      api.patchChecagemResponsavel(id, responsavel_usuario_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHECAGENS_QUERY_KEY] });
    },
  });
};

// Mutation para criar uma campanha para a checagem
export const useCreateChecagemCampanha = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { canal: 'email'|'whatsapp'; nome?: string; observacao?: string } }) => 
      api.postChecagemCampanha(id, data),
  });
};

// Mutation para adicionar um imóvel à checagem
export const useAddImovelToChecagem = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ checagemId, imovel_id }: { checagemId: number; imovel_id: number }) => 
            api.postAddImovel(checagemId, imovel_id),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [CHECAGENS_QUERY_KEY, variables.checagemId, 'imoveis'] });
        },
    });
};

// Mutation para atualizar um item do checklist
export const useUpdateChecklistItem = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ ciId, canal, data }: { ciId: number; canal: CanalChecklist; data: { status_canal: StatusCanal; anotacoes?: string }}) =>
            api.patchChecklist(ciId, canal, data),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['checagemImovel', variables.ciId, 'checklist'] });
            // Invalida a query de imóveis para atualizar a barra de progresso
            // Assumindo que o checagemId pode ser extraído de algum lugar ou passado na mutação
            // queryClient.invalidateQueries([CHECAGENS_QUERY_KEY, checagemId, 'imoveis']);
        }
    });
};

// Mutation para criar uma nova tarefa
export const useCreateTarefa = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ ciId, data }: { ciId: number; data: any }) => api.postTarefa(ciId, data),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['checagemImovel', variables.ciId, 'tarefas'] });
        }
    });
};

// Mutation para atualizar o status de uma tarefa
export const useUpdateTarefaStatus = (ciId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ tarefaId, status }: { tarefaId: number; status: StatusTarefa }) => 
            api.patchTarefa(tarefaId, { status }),
        onMutate: async ({ tarefaId, status }) => {
            await queryClient.cancelQueries({ queryKey: ['checagemImovel', ciId, 'tarefas'] });
            const previousTarefas = queryClient.getQueryData<Tarefa[]>(['checagemImovel', ciId, 'tarefas']);
            queryClient.setQueryData<Tarefa[]>(['checagemImovel', ciId, 'tarefas'], old =>
                old?.map(t => t.id === tarefaId ? { ...t, status } : t)
            );
            return { previousTarefas };
        },
        onError: (err, variables, context) => {
            if (context?.previousTarefas) {
                queryClient.setQueryData(['checagemImovel', ciId, 'tarefas'], context.previousTarefas);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['checagemImovel', ciId, 'tarefas'] });
        },
    });
};

// Mutation para marcar captação
export const useMarcarCaptacao = () => {
    return useMutation({
        mutationFn: ({ ciId, resultado }: { ciId: number; resultado: string }) => 
            api.postCaptacao(ciId, { resultado }),
        onSuccess: (data, variables) => {
            // Invalida a query de imóveis e de eventos
            // queryClient.invalidateQueries(['imoveis', imovelId, 'eventos']);
            // queryClient.invalidateQueries([CHECAGENS_QUERY_KEY, checagemId, 'imoveis']);
        }
    });
};

// Mutation para criar uma nova checagem
export const useCreateChecagem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { nome?: string; descricao: string; status: string }) => api.postChecagem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHECAGENS_QUERY_KEY] });
    },
  });
};

export const useFinalizarChecagem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.finalizarChecagem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHECAGENS_QUERY_KEY] });
    },
  });
};
